import { createContext, useCallback, useContext, useState } from "react";

export const SessionContextPanelType = {
  AGENT: "agent",
  DATASET: "dataset",
  APPS: "apps",
} as const;
export type SessionContextPanelType =
  (typeof SessionContextPanelType)[keyof typeof SessionContextPanelType];

const DEFAULT_VIEW = "overview";

interface SessionContextPanelState {
  type: SessionContextPanelType;
  id: string;
  // Which sub-view is showing within the entity's detail (e.g. "overview",
  // "skills"). Always defined once a panel is open — defaults to "overview".
  view: string;
}

interface UseSessionContextPanelResult {
  // null means the sidebar shows its table-of-contents (home) view.
  panel: SessionContextPanelState | null;
  openPanel: (type: SessionContextPanelType, id: string) => void;
  setView: (view: string) => void;
  goHome: () => void;
}

// Shared so the sidebar (mounted alongside `<Outlet>` in ProjectLayout) and
// the MCP-apps sync hook (mounted deep inside the routed Chat) can both
// drive which entity/app the sidebar shows, despite neither being an
// ancestor of the other.
const SessionContextPanelContext = createContext<UseSessionContextPanelResult | null>(null);

export const SessionContextPanelProvider = ({ children }: { children: React.ReactNode }) => {
  const [panel, setPanel] = useState<SessionContextPanelState | null>(null);

  // Stable across renders (functional updates only) so effects that depend on
  // these — e.g. `useMcpAppsPaneSync`'s live-subscription effect — don't
  // re-run, and reset their own state, every time the panel changes.
  const openPanel = useCallback((type: SessionContextPanelType, id: string) => {
    setPanel({ type, id, view: DEFAULT_VIEW });
  }, []);

  const setView = useCallback((view: string) => {
    setPanel((prev) => (prev ? { ...prev, view } : prev));
  }, []);

  const goHome = useCallback(() => {
    setPanel(null);
  }, []);

  return (
    <SessionContextPanelContext.Provider value={{ panel, openPanel, setView, goHome }}>
      {children}
    </SessionContextPanelContext.Provider>
  );
};

// Backs the session context sidebar's TOC/detail state and which
// dataset/agent/app + sub-view it's showing.
export function useSessionContextPanel(): UseSessionContextPanelResult {
  const ctx = useContext(SessionContextPanelContext);
  if (!ctx) {
    throw new Error("useSessionContextPanel must be used within a SessionContextPanelProvider");
  }
  return ctx;
}
